import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal, Placeholder } from "@/components/primitives";
import { StrategiesGrid } from "@/components/StrategyCard";
import { getContent, getSettings, trackEvent } from "@/lib/api";
import { ArrowRight } from "@phosphor-icons/react";

const STACK = [
  ["Artificial Intelligence", "ML models for statistical deviations & signal detection"],
  ["Quantitative Analysis", "Relative-value, basis, and correlation modeling"],
  ["Automated Execution", "Configurable execution against connected infrastructure"],
  ["Blockchain Infrastructure", "Multi-chain monitoring incl. Ethereum, Tron and more"],
  ["Cloud Computing", "Scalable classical compute for parallel analysis"],
  ["Software Engineering", "Reliability, observability, and continuous delivery"],
];

export default function Technology() {
  const [exchanges, setExchanges] = useState([]);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    trackEvent("page_view", { page: "technology" });
    getContent("exchanges").then(setExchanges).catch(() => {});
    getSettings().then(setSettings).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Technology"
        title="AI-powered arbitrage infrastructure, accessible through software."
        sub="Quantum Scalp is fundamentally a technology company. We combine AI, quantitative analysis, automated execution, and blockchain infrastructure into one software ecosystem — while transparently explaining the risks of automated trading."
      />

      <Section grid>
        <SectionHeading eyebrow="The Stack" title="One technology ecosystem." className="mb-12" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {STACK.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.05}>
              <div className="qs-beam h-full rounded-xl border border-white/10 bg-[#0A0B10] p-6">
                <p className="font-mono text-xs text-[#0E9E90] mb-3">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="text-lg font-bold text-white mb-2">{t}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section grid>
        <SectionHeading
          eyebrow="Capability"
          title="Six parallel arbitrage strategies."
          sub="Q-Core evaluates multiple arbitrage types simultaneously. Each represents a technology capability — not a promise of returns."
          className="mb-12"
        />
        <StrategiesGrid />
      </Section>

      <Section grid>
        <SectionHeading
          eyebrow="Supported Venues"
          title="Markets the engine is designed to monitor."
          sub="The list below is illustrative. Venue support is maintained by the company and must be independently verified before launch."
          className="mb-8"
        />
        <div className="mb-6"><Placeholder>[INSERT VERIFIED EXCHANGE LIST]</Placeholder></div>
        <div className="flex flex-wrap gap-3" data-testid="exchange-list">
          {exchanges.map((e) => (
            <span key={e.id} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0A0B10] px-4 py-2 text-sm text-[#cbd5e1]">
              <span className={`font-mono text-[10px] ${e.category === "DEX" ? "text-[#0E9E90]" : "text-[#2DD4BF]"}`}>{e.category}</span>
              {e.name}
              {!e.verified && <span className="font-mono text-[9px] text-amber-300/70">· unverified</span>}
            </span>
          ))}
        </div>
      </Section>

      <Section>
        <div className="rounded-2xl border border-white/10 bg-[#0A0B10] p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl font-black text-white">Go deeper into the engine.</h3>
            <p className="text-[#94A3B8] mt-2">See how Q-Core analyzes, detects, and executes.</p>
          </div>
          <Link to="/q-core" className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors">
            Inside Q-Core <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </Section>
    </div>
  );
}
